// =============================================================================
// BackButton (WS-9) — the 44px, never-dead-end back button, extracted so it can
// be dropped into any header in place (the ~140 ad-hoc `p-2` ~36px back buttons)
// without restructuring the whole header. <PageHeader> uses it too, so the
// smart-back logic lives in exactly one place.
//
// Back behaviour: onClick (explicit handler, e.g. close a sub-view) > to
// (explicit route) > smart back — go back inside the app when there's app
// history, else fall to the dashboard so a deep-linked page can't dead-end out.
// React Router records a session index on history.state; idx 0 = landed here
// directly (deep link / fresh load).
//
// `to` REPLACES the current entry rather than pushing: an up-arrow must not
// add history depth. A push here leaves [list, detail, list], so the list's
// own smart-back pops straight back into the detail you just left and the two
// arrows ping-pong forever (the post-#347 "infinite loop" report).
// =============================================================================

import { useNavigate, type NavigateFunction } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import clsx from 'clsx'
import { confirmLeavePage, pageIsDirty } from '@/lib/pageDirty'

/** Exported for the full-page report shells (<ReportUnavailable/>), which take
 *  an `onBack` callback rather than rendering a <BackButton/>: they must not
 *  hard-dump to the dashboard, and there is nothing to be gained from a second
 *  copy of this rule.
 *
 *  `fallback` is where a page with NO app history behind it (a typed or shared
 *  link, a fresh load) goes instead of the dashboard — the pallet page passes
 *  `/pallets` (F09 UF-05). Given one, it REPLACES like a `to` does, so the
 *  up-move adds no depth; omitted, the push to `/` is exactly what it was. */
export function smartBack(navigate: NavigateFunction, fallback?: string) {
  const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
  if (idx > 0) navigate(-1)
  else if (fallback) navigate(fallback, { replace: true })
  else navigate('/')
}

export default function BackButton({ onClick, to, className, surface = 'shell', inPage = false, label }: {
  onClick?: () => void
  to?: string
  className?: string
  /** What the button sits on — the same prop name and idea as <Modal/>,
   *  <QueryStateGate/> and <SignaturePad/>. The arrow takes its colour from
   *  the header either way; `light` swaps the hover wash, because the dark
   *  shell's gray-800 wash under a dark arrow on a white header hides it. */
  surface?: 'shell' | 'light'
  /** The arrow swaps a view INSIDE the page (a print preview back to its
   *  form) and leaves nothing, so the leave guard below does not apply: asking
   *  "Discard unsaved changes?" there names a loss that never happens, and
   *  teaches the operator to tap Discard unread (SIZA's print view). Needs an
   *  `onClick` — a `to` or smart back always leaves. */
  inPage?: boolean
  /** Names where the arrow goes ("Field run · 15 Sep – 12 Oct 2026"), shown beside it. */
  label?: string
}) {
  const navigate = useNavigate()
  const handle = onClick ?? (to ? () => navigate(to, { replace: true }) : () => smartBack(navigate))
  // The app-wide unsaved-work guard (src/lib/pageDirty.ts). This button is the
  // one control every page header shares, so guarding it here covers ~200 pages
  // that would otherwise each need their own confirm.
  //
  // The clean path stays SYNCHRONOUS and byte-for-byte what it was: no page has
  // armed the guard, pageIsDirty() is false, handle() runs on this same tick.
  // Deferring every back tap through a promise for the sake of the rare dirty
  // one would change the timing of every page in the app to no purpose.
  function guardedBack() {
    if ((inPage && onClick) || !pageIsDirty()) { handle(); return }
    void confirmLeavePage().then(ok => { if (ok) handle() })
  }
  return (
    <button onClick={guardedBack} aria-label="Back"
      className={clsx('-ml-2 min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-xl pressable',
        label && 'gap-1.5 pl-2 pr-3 text-sm',
        surface === 'light' ? 'hover:bg-gray-200' : 'hover:bg-surface', className)}>
      <ArrowLeft size={20} />
      {label && <span className="truncate max-w-[16rem]">{label}</span>}
    </button>
  )
}
